import React from "react";
import { LiveArena } from "@/hooks/useLiveArenas";
import { Users, Mic, Video, Flame, TrendingUp, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LiveBadge } from "./LiveBadge";
import { ArenaTypeBadge } from "./ArenaTypeBadge";
import { cn } from "@/lib/utils";

interface ArenaCardProps {
  arena: LiveArena & { arena_type?: string; is_trending?: boolean };
  onJoin: () => void;
  previewFaces?: string[];
}

const ArenaCard: React.FC<ArenaCardProps> = ({ arena, onJoin, previewFaces = [] }) => {
  const isFull = arena.participants_count >= arena.max_participants;
  const isActive = arena.participants_count > 0;
  const isHot = arena.participants_count >= arena.max_participants * 0.7;
  
  // Topic colors
  const getTopicColor = (topic: string) => {
    const colors: Record<string, string> = {
      "Flirting": "from-pink-500 to-rose-500",
      "Relationships": "from-purple-500 to-violet-500",
      "Travel": "from-blue-500 to-cyan-500",
      "Late Night": "from-indigo-500 to-purple-500",
      "Casual": "from-green-500 to-emerald-500",
      "Adventure": "from-orange-500 to-amber-500",
      "Food & Dining": "from-red-500 to-orange-500",
      "Music": "from-violet-500 to-fuchsia-500",
    };
    return colors[topic] || "from-primary to-secondary";
  };

  return (
    <div 
      className={cn(
        "relative rounded-2xl overflow-hidden backdrop-blur-xl bg-white/50 border border-white/60 shadow-lg transition-all duration-300 hover:scale-[1.02] hover:shadow-xl",
        isActive && "ring-2 ring-pop/30 ring-offset-2 ring-offset-transparent"
      )}
    >
      {/* Pulse animation for active arenas */}
      {isActive && (
        <div className="absolute inset-0 rounded-2xl">
          <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-pop/10 to-primary/5 animate-pulse" />
        </div>
      )}

      {/* Live badge with glow */}
      {isActive && (
        <div className="absolute top-3 right-3 z-10">
          <LiveBadge variant={isHot ? "glow" : "default"} />
        </div>
      )}

      {/* Topic badge */}
      <div 
        className={`absolute top-3 left-3 px-2 py-0.5 rounded-full text-white text-xs font-semibold bg-gradient-to-r ${getTopicColor(arena.topic)} z-10`}
      >
        {arena.topic}
      </div>

      {/* Arena type badge */}
      {arena.arena_type && (
        <div className="absolute top-12 left-3 z-10">
          <ArenaTypeBadge type={arena.arena_type as any} />
        </div>
      )}

      {/* Trending/New badge */}
      {arena.is_trending && (
        <div className="absolute top-12 right-3 z-10 flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500 text-white text-xs font-medium">
          <TrendingUp className="w-3 h-3" />
          <span>Trending</span>
        </div>
      )}

      {/* Content */}
      <div className="relative pt-12 p-4">
        <h3 className="font-bold text-lg text-foreground mb-1 mt-6">{arena.name}</h3>
        {arena.description && (
          <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{arena.description}</p>
        )}

        {/* Stats row */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            {/* Preview faces with pulse on active */}
            <div className="flex -space-x-2">
              {(previewFaces.length > 0 ? previewFaces : [
                "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop",
                "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop",
                "https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=100&h=100&fit=crop",
              ]).slice(0, 4).map((face, i) => (
                <img
                  key={i}
                  src={face}
                  alt=""
                  className={cn(
                    "w-7 h-7 rounded-full border-2 border-white object-cover",
                    isActive && i === 0 && "ring-2 ring-green-500 ring-offset-1"
                  )}
                />
              ))}
            </div>
            <div className="flex items-center gap-1">
              <span className="text-sm">
                <span className={cn(
                  "font-semibold",
                  isHot ? "text-orange-500" : "text-foreground"
                )}>
                  {arena.participants_count}
                </span>
                <span className="text-muted-foreground">/{arena.max_participants}</span>
              </span>
              {isHot && <Flame className="w-4 h-4 text-orange-500 animate-pulse" />}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Video/Voice indicator */}
            <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-xs">
              {arena.is_video_enabled ? (
                <>
                  <Video className="w-3 h-3" />
                  <span>Video</span>
                </>
              ) : (
                <>
                  <Mic className="w-3 h-3" />
                  <span>Voice</span>
                </>
              )}
            </span>

            {/* Language tag */}
            <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground uppercase">
              {arena.language}
            </span>
          </div>
        </div>

        {/* Country/City tag if present */}
        {arena.country_code && (
          <div className="flex items-center gap-1 mb-3 text-xs text-muted-foreground">
            <Globe className="w-3 h-3" />
            <span>{arena.country_code}</span>
          </div>
        )}

        {/* Join button */}
        <Button
          onClick={onJoin}
          disabled={isFull && !arena.is_joined}
          className={cn(
            "w-full rounded-full transition-all",
            arena.is_joined 
              ? "bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
              : isFull
                ? "bg-muted text-muted-foreground cursor-not-allowed"
                : "bg-gradient-to-r from-pop to-primary hover:from-pop/90 hover:to-primary/90 shadow-lg shadow-pop/25"
          )}
        >
          {arena.is_joined ? (
            <>
              <span className="relative flex h-2 w-2 mr-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
              </span>
              In Arena
            </>
          ) : isFull ? (
            "Arena Full"
          ) : (
            <>
              <Users className="w-4 h-4 mr-2" />
              Join Arena
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default ArenaCard;
